/**
 * useMemberFiltering - Member filtering and processing hook for Roster feature
 * 
 * This hook manages all member filtering, processing, and display logic.
 * It uses the MemberDataProcessor service for data transformation.
 * 
 * Key responsibilities:
 * - Filter members based on various criteria
 * - Process member data for display
 * - Handle tag conversion and validation
 * - Provide filtered and processed member data
 * - Manage filter state and performance optimization
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { MemberDataProcessor } from '../services/MemberDataProcessor';
import { filterMembers, sortMembers } from '../utils/rosterCalculations';

// Create singleton processor instance
const memberDataProcessor = new MemberDataProcessor();

export function useMemberFiltering(roster, unassignedRoles, filterState, memoizedOutcomeAllocations, availableTags) {
  // State for processing status
  const [processingStats, setProcessingStats] = useState({
    lastProcessTime: null,
    processedMembers: 0,
    filterTime: 0
  });
  
  /**
   * Process roster members with allocation data
   */
  const processedRosterData = useMemo(() => {
    const startTime = performance.now();
    
    // Don't process if tags haven't loaded yet (prevents showing IDs)
    // But allow processing if we have roster data and tags are still loading
    if (availableTags.length === 0 && roster.length > 0) {
      console.log('useMemberFiltering: Tags not loaded yet, but processing with empty tags to show roster data');
      // Continue processing with empty tags rather than blocking
    }
    
    console.log('📊 useMemberFiltering: Processing roster data...', {
      roster: roster.length,
      unassignedRoles: unassignedRoles.length,
      availableTags: availableTags.length,
      memoizedAllocations: memoizedOutcomeAllocations.size,
      rosterSample: roster.slice(0, 2).map(m => ({ id: m.id, name: m.name })),
      tagsAvailable: availableTags.length > 0
    });
    
    try {
      // Process each member with allocation data
      const processedMembers = roster.map(member => {
        const memberOutcomes = memoizedOutcomeAllocations.get(member.id) || {};
        return memberDataProcessor.processMemberWithAllocations(member, memberOutcomes, availableTags);
      });
      
      const endTime = performance.now();
      setProcessingStats({
        lastProcessTime: endTime - startTime,
        processedMembers: processedMembers.length,
        filterTime: 0
      });
      
      console.log('✅ useMemberFiltering: Processed', processedMembers.length, 'members in', (endTime - startTime).toFixed(2), 'ms');
      return processedMembers;
      
    } catch (error) {
      console.error('❌ useMemberFiltering: Error processing roster data:', error);
      return [];
    }
  }, [roster, memoizedOutcomeAllocations, availableTags]);
  
  /**
   * Apply filters to processed roster data
   */
  const filteredRosterData = useMemo(() => {
    const startTime = performance.now();
    
    if (processedRosterData.length === 0) {
      return [];
    }
    
    console.log('📊 useMemberFiltering: Applying filters...', {
      totalMembers: processedRosterData.length,
      filters: {
        search: !!filterState.debouncedSearchName,
        company: filterState.companyFilter.length,
        location: filterState.locationFilter.length,
        function: filterState.functionFilter.length,
        outcome: filterState.outcomeFilter.length,
        application: filterState.applicationFilter.length,
        tag: filterState.tagFilter.length,
        allocationStatus: filterState.allocationStatusFilter.length
      }
    });
    
    try {
      let filtered = [...processedRosterData];
      
      // Apply search filter
      if (filterState.debouncedSearchName) {
        const searchTerm = filterState.debouncedSearchName.toLowerCase();
        filtered = filtered.filter(member => 
          (member.name || '').toLowerCase().includes(searchTerm)
        );
      }
      
      // Apply company filter
      if (filterState.companyFilter.length > 0) {
        filtered = filtered.filter(member => 
          filterState.companyFilter.includes(member.company)
        );
      }
      
      // Apply location filter
      if (filterState.locationFilter.length > 0) {
        filtered = filtered.filter(member => 
          filterState.locationFilter.includes(member.location)
        );
      }
      
      // Apply function filter
      if (filterState.functionFilter.length > 0) {
        filtered = filtered.filter(member => 
          filterState.functionFilter.includes(member.function)
        );
      }
      
      // Apply outcome filter
      if (filterState.outcomeFilter.length > 0 && !filterState.outcomeFilter.includes('all')) {
        filtered = filtered.filter(member => 
          member.outcomeNames.some(outcome => filterState.outcomeFilter.includes(outcome))
        );
      }
      
      // Apply application filter
      if (filterState.applicationFilter.length > 0 && !filterState.applicationFilter.includes('all')) {
        filtered = filtered.filter(member => {
          const memberApps = Array.from(member.memberApplications);
          return memberApps.some(app => filterState.applicationFilter.includes(app));
        });
      }
      
      // Apply tag filter
      if (filterState.tagFilter.length > 0 && !filterState.tagFilter.includes('all')) {
        filtered = filtered.filter(member => {
          const memberTagNames = Array.from(member.tagNames);
          return memberTagNames.some(tag => filterState.tagFilter.includes(tag));
        });
      }
      
      // Apply allocation status filter
      if (filterState.allocationStatusFilter.length > 0) {
        filtered = filtered.filter(member =>
          filterState.allocationStatusFilter.includes(member.allocationStatus)
        );
      }

      // Apply department filter
      if (filterState.departmentFilter && filterState.departmentFilter.length > 0) {
        filtered = filtered.filter(member =>
          filterState.departmentFilter.includes(member.department)
        );
      }
      
      // Sort the filtered results
      const sorted = filtered.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      
      const endTime = performance.now();
      setProcessingStats(prev => ({
        ...prev,
        filterTime: endTime - startTime
      }));
      
      console.log('✅ useMemberFiltering: Filtered to', sorted.length, 'members in', (endTime - startTime).toFixed(2), 'ms');
      return sorted;
      
    } catch (error) {
      console.error('❌ useMemberFiltering: Error filtering roster data:', error);
      return processedRosterData; // Return unfiltered data as fallback
    }
  }, [processedRosterData, filterState]);
  
  /**
   * Add unassigned roles if requested
   */
  const finalRosterData = useMemo(() => {
    const shouldShowStaffed = filterState.allocationTypeFilter.includes('staffed');
    const shouldShowUnassigned = filterState.allocationTypeFilter.includes('unassigned');
    
    let baseData = [];
    
    // Add staffed members if requested
    if (shouldShowStaffed) {
      baseData = [...filteredRosterData];
    }
    
    // Add unassigned roles if requested
    if (shouldShowUnassigned && unassignedRoles.length > 0) {
      console.log('📊 useMemberFiltering: Adding', unassignedRoles.length, 'unassigned roles');
      
      const processedUnassignedRoles = unassignedRoles.map(role => {
        // Process unassigned role as a member-like object
        const processedRole = {
          id: `unassigned-${role.unassignedRoleData?.id || role.id}`,
          name: `${role.unassignedRoleData?.role_name || role.name || 'Unknown Role'} (Unassigned)`,
          company: role.company || 'CA',
          location: role.location || 'US',
          function: role.function || 'Other',
          role: role.unassignedRoleData?.role_name || role.role || role.name || '',
          isUnassigned: true,
          unassignedRoleData: role.unassignedRoleData || role,
          
          // Calculate allocations if available
          staffedAllocations: role.unassignedRoleData?.monthly_allocations || Array(12).fill(0),
          outcomeNames: [role.outcome_name].filter(Boolean),
          memberApplications: new Set(),
          tagNames: new Set(),
          allocationStatus: 'Unassigned Role',
          maxAllocation: Math.max(...(role.unassignedRoleData?.monthly_allocations || [0])),
          overAllocation: {
            hasOverAllocation: false,
            maxAllocation: 0
          },
          
          // Display fields
          displayLocation: role.location || 'Various',
          displayFunction: role.function || 'Other',
          
          // Validation
          isValid: true,
          validationErrors: []
        };
        
        return processedRole;
      });
      
      baseData = [...baseData, ...processedUnassignedRoles];
    }
    
    console.log('📊 useMemberFiltering: Final roster data:', {
      staffed: shouldShowStaffed ? filteredRosterData.length : 0,
      unassigned: shouldShowUnassigned ? unassignedRoles.length : 0,
      total: baseData.length
    });
    
    return baseData;
  }, [filteredRosterData, unassignedRoles, filterState.allocationTypeFilter]);
  
  /**
   * Calculate active filters count
   */
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filterState.debouncedSearchName) count++;
    if (filterState.companyFilter.length > 0) count++;
    if (filterState.locationFilter.length > 0) count++;
    if (filterState.functionFilter.length > 0) count++;
    if (filterState.outcomeFilter.length > 0) count++;
    if (filterState.applicationFilter.length > 0) count++;
    if (filterState.tagFilter.length > 0) count++;
    if (filterState.allocationStatusFilter.length > 0) count++;
    return count;
  }, [filterState]);
  
  /**
   * Calculate filter statistics
   */
  const getFilterStatistics = useCallback(() => {
    return {
      totalMembers: roster.length,
      processedMembers: processedRosterData.length,
      filteredMembers: filteredRosterData.length,
      finalMembers: finalRosterData.length,
      unassignedRoles: unassignedRoles.length,
      activeFilters: activeFiltersCount,
      processingStats,
      filterEfficiency: processedRosterData.length > 0 ? 
        ((processedRosterData.length - filteredRosterData.length) / processedRosterData.length * 100).toFixed(1) + '%' : 
        '0%'
    };
  }, [roster.length, processedRosterData.length, filteredRosterData.length, finalRosterData.length, unassignedRoles.length, activeFiltersCount, processingStats]);
  
  /**
   * Get member by ID from processed data
   */
  const getMemberById = useCallback((memberId) => {
    return finalRosterData.find(member => member.id === memberId);
  }, [finalRosterData]);
  
  /**
   * Get members by criteria
   */
  const getMembersByCriteria = useCallback((criteria) => {
    return filterMembers(finalRosterData, criteria);
  }, [finalRosterData]);
  
  /**
   * Sort members by field
   */
  const getSortedMembers = useCallback((sortBy = 'name', sortDirection = 'asc') => {
    return sortMembers(finalRosterData, sortBy, sortDirection);
  }, [finalRosterData]);
  
  /**
   * Get member summary by category
   */
  const getMemberSummaryByCategory = useCallback((category) => {
    const summary = {};
    
    finalRosterData.forEach(member => {
      let key;
      switch (category) {
        case 'company':
          key = member.company;
          break;
        case 'location':
          key = member.displayLocation;
          break;
        case 'function':
          key = member.displayFunction;
          break;
        case 'allocationStatus':
          key = member.allocationStatus;
          break;
        default:
          key = member[category] || 'Unknown';
      }
      
      summary[key] = (summary[key] || 0) + 1;
    });
    
    return summary;
  }, [finalRosterData]);
  
  /**
   * Clear processing cache
   */
  const clearProcessingCache = useCallback(() => {
    console.log('🧹 useMemberFiltering: Clearing processing cache');
    memberDataProcessor.clearCache();
    setProcessingStats({
      lastProcessTime: null,
      processedMembers: 0,
      filterTime: 0
    });
  }, []);
  
  /**
   * Get processing performance stats
   */
  const getProcessingStats = useCallback(() => {
    const processorStats = memberDataProcessor.getCacheStats();
    return {
      ...processingStats,
      processorStats,
      filterStatistics: getFilterStatistics()
    };
  }, [processingStats, getFilterStatistics]);
  
  // Clear cache when tags change significantly
  useEffect(() => {
    if (availableTags.length > 0) {
      // Tags have loaded, we can clear any stale cache
      memberDataProcessor.clearCache();
    }
  }, [availableTags.length]);
  
  return {
    // Processed data
    processedRosterData,
    filteredRosterData,
    finalRosterData,
    
    // Statistics
    activeFiltersCount,
    getFilterStatistics,
    getProcessingStats,
    
    // Utilities
    getMemberById,
    getMembersByCriteria,
    getSortedMembers,
    getMemberSummaryByCategory,
    
    // Cache management
    clearProcessingCache,
    
    // Performance metrics
    processingStats
  };
}